import api from '@/lib/axios';

export interface WorkspaceUser {
  id: string;
  user_id: string;
  email: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  status: 'active' | 'invited' | 'suspended' | 'disabled';
  joined_at: string;
  invited_by?: string;
  invitation_id?: string; // ID of the invitation record (for resending)
  invitation_expires_at?: string; // ISO date string for invitation expiry (bonus feature)
}

export interface InviteUserRequest {
  email: string;
  first_name?: string;
  role: 'admin' | 'member' | 'viewer';
}

export interface UpdateUserRequest {
  role?: 'owner' | 'admin' | 'member' | 'viewer';
  status?: 'active' | 'suspended' | 'disabled';
}

/**
 * Fetch all users in a workspace
 * 
 * @param workspaceId - The workspace ID
 * @returns Array of workspace users
 */
export async function fetchWorkspaceUsers(workspaceId: string): Promise<WorkspaceUser[]> {
  try {
    const response = await api.get<WorkspaceUser[]>(`/workspaces/${workspaceId}/users`);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error: any) {
    console.error('[fetchWorkspaceUsers] Error:', error);
    if (error.response?.status === 404) {
      console.warn(`Users endpoint not available for workspace ${workspaceId}, returning empty array`);
      return [];
    }
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch users');
  }
}

/**
 * Invite a user to a workspace
 * 
 * @param workspaceId - The workspace ID
 * @param email - User email address
 * @param role - User role (admin, member, viewer)
 * @param first_name - Optional first name
 * @returns Created user/invitation
 */
export async function inviteUser(
  workspaceId: string,
  email: string,
  role: 'admin' | 'member' | 'viewer',
  first_name?: string
): Promise<WorkspaceUser> {
  try {
    const payload: { email: string; role: string; first_name?: string } = {
      email: email.trim(),
      role,
    };
    if (first_name?.trim()) {
      payload.first_name = first_name.trim();
    }
    // Use invitations endpoint as specified
    const response = await api.post<WorkspaceUser>(`/workspaces/${workspaceId}/invitations`, payload);
    return response.data;
  } catch (error: any) {
    console.error('[inviteUser] Error:', error);
    // Handle duplicate invite error
    if (error.response?.status === 409 || error.response?.status === 400) {
      const message = error.response?.data?.message || 'User is already a member of this workspace';
      throw new Error(message);
    }
    throw new Error(error.response?.data?.message || error.message || 'Failed to invite user');
  }
}

/**
 * Update a workspace user
 * 
 * @param workspaceId - The workspace ID
 * @param userId - The user ID to update
 * @param payload - Update payload (role and/or status)
 * @returns Updated user
 */
export async function updateWorkspaceUser(
  workspaceId: string,
  userId: string,
  payload: UpdateUserRequest
): Promise<WorkspaceUser> {
  try {
    const response = await api.put<WorkspaceUser>(`/workspaces/${workspaceId}/users/${userId}`, payload);
    return response.data;
  } catch (error: any) {
    console.error('[updateWorkspaceUser] Error:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to update user');
  }
}

/**
 * Remove a user from a workspace
 * 
 * @param workspaceId - The workspace ID
 * @param userId - The user ID to remove
 */
export async function removeWorkspaceUser(workspaceId: string, userId: string): Promise<void> {
  try {
    await api.delete(`/workspaces/${workspaceId}/users/${userId}`);
  } catch (error: any) {
    console.error('[removeWorkspaceUser] Error:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to remove user');
  }
}

/**
 * Resend an invitation to a workspace
 * 
 * @param workspaceId - The workspace ID
 * @param invitationId - The invitation ID to resend
 */
export async function resendWorkspaceInvitation(
  workspaceId: string,
  invitationId: string
): Promise<void> {
  try {
    await api.post(`/workspaces/${workspaceId}/invitations/${invitationId}/resend`);
  } catch (error: any) {
    console.error('[resendWorkspaceInvitation] Error:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to resend invitation');
  }
}
